import { useState } from "react";
import axios from "axios";
import { Droplets, Facebook, Youtube, Send, MapPin, Phone, Mail } from "lucide-react";
import { Link } from "react-router-dom";
import { Input } from "./ui/input";
import { churchInfo } from "../mock";
import { useToast } from "../hooks/use-toast";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Footer() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const { toast } = useToast();

  const onSub = async (e) => {
    e.preventDefault();
    if (!email || !email.includes("@")) {
      toast({ title: "Enter a valid email", description: "We'd love to keep you updated." });
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/subscribers`, { email });
      toast({ title: "You're on the list!", description: "We'll send updates, prayer alerts, and special services." });
      setEmail("");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Please try again.";
      toast({ title: "Couldn't subscribe", description: typeof msg === "string" ? msg : "Please try again." });
    } finally {
      setBusy(false);
    }
  };

  return (
    <footer id="contact" className="bg-[#0a2229] text-white pt-20 pb-8">
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-4">
          <div className="flex items-center gap-3">
            <img src="/brand/logo-light-192.png" alt="First Assembly Living Waters" className="h-14 w-14 object-contain rounded-full" />
            <div>
              <div className="font-bold text-xl">Living Waters</div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-white/60">First Assembly • Big Spring, TX</div>
            </div>
          </div>
          <p className="mt-5 text-white/70 leading-relaxed text-[15px] max-w-sm">
            An Assemblies of God family rooted in Scripture and led by the Spirit. Come thirsty. Leave full.
          </p>
          <div className="mt-6 flex items-center gap-3">
            <a href={churchInfo.facebook} target="_blank" rel="noreferrer" aria-label="Facebook"
              className="h-10 w-10 rounded-full border border-white/20 flex items-center justify-center hover:bg-[#c8a36a] hover:text-[#0a2229] hover:border-[#c8a36a] transition-colors">
              <Facebook className="h-4 w-4" />
            </a>
            <a href={churchInfo.youtube} target="_blank" rel="noreferrer" aria-label="YouTube"
              className="h-10 w-10 rounded-full border border-white/20 flex items-center justify-center hover:bg-[#c8a36a] hover:text-[#0a2229] hover:border-[#c8a36a] transition-colors">
              <Youtube className="h-4 w-4" />
            </a>
            <a href={churchInfo.telegram} target="_blank" rel="noreferrer" aria-label="Telegram"
              className="h-10 w-10 rounded-full border border-white/20 flex items-center justify-center hover:bg-[#c8a36a] hover:text-[#0a2229] hover:border-[#c8a36a] transition-colors">
              <Send className="h-4 w-4" />
            </a>
          </div>
        </div>

        <div className="lg:col-span-2">
          <div className="text-[#c8a36a] uppercase tracking-[0.28em] text-xs font-semibold mb-5">Explore</div>
          <ul className="space-y-3 text-sm text-white/80">
            <li><Link to="/" className="hover:text-[#c8a36a]">Home</Link></li>
            <li><Link to="/what-we-believe" className="hover:text-[#c8a36a]">What We Believe</Link></li>
            <li><Link to="/videos" className="hover:text-[#c8a36a]">Sermons</Link></li>
            <li><Link to="/staff" className="hover:text-[#c8a36a]">Staff & Leaders</Link></li>
            <li><a href="/#prayer" className="hover:text-[#c8a36a]">Prayer Requests</a></li>
            <li><a href={churchInfo.donateUrl} target="_blank" rel="noreferrer" className="hover:text-[#c8a36a]">Give Online</a></li>
          </ul>
        </div>

        <div className="lg:col-span-3">
          <div className="text-[#c8a36a] uppercase tracking-[0.28em] text-xs font-semibold mb-5">Visit & Contact</div>
          <ul className="space-y-4 text-sm text-white/80">
            <li className="flex items-start gap-2.5"><MapPin className="h-4 w-4 text-[#c8a36a] mt-0.5" />{churchInfo.address}</li>
            <li className="flex items-center gap-2.5"><Phone className="h-4 w-4 text-[#c8a36a]" /><a href={`tel:${churchInfo.phoneRaw}`} className="hover:text-[#c8a36a]">{churchInfo.phone}</a></li>
            <li className="flex items-center gap-2.5"><Mail className="h-4 w-4 text-[#c8a36a]" /><a href={`mailto:${churchInfo.email}`} className="hover:text-[#c8a36a] break-all">{churchInfo.email}</a></li>
          </ul>
        </div>

        <div className="lg:col-span-3">
          <div className="text-[#c8a36a] uppercase tracking-[0.28em] text-xs font-semibold mb-5">Stay Connected</div>
          <p className="text-sm text-white/70 mb-4">Sign up for updates, prayer alerts, and special services.</p>
          <form onSubmit={onSub} className="flex gap-2">
            <Input value={email} onChange={(e) => setEmail(e.target.value)} type="email" placeholder="Email address"
              className="bg-white/5 border-white/15 text-white placeholder:text-white/40" />
            <button type="submit" disabled={busy} className="btn-gold rounded-md px-4 font-semibold text-sm disabled:opacity-60">{busy ? "..." : "Sign up"}</button>
          </form>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 mt-14 pt-6 border-t border-white/10 flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-white/50">
        <div>© {new Date().getFullYear()} First Assembly Living Waters — All Rights Reserved.</div>
        <div className="font-semibold text-sm text-[#c8a36a]">“Come to the waters.” — Isaiah 55:1</div>
      </div>
    </footer>
  );
}
